import { Button, Menu, MenuItem, Popover, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import * as XLSX from "xlsx";
import { useMemo, useState } from "react";

import { AdminViewApi } from "../../api/AdminViewApi";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { LabeledSelect } from "../../components/labeledSelect/LabeledSelect";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import styles from "./AdminViewSection.module.scss";

const YEAR_RANGE = 10;

const buildYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: YEAR_RANGE }, (_, index) => currentYear - index);
};

export const DownloadHistorySection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const [selectedYear, setSelectedYear] = useState<number>(yearOptions[0]);
  const [downloadHistoryData, setDownloadHistoryData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);

  const fetchDownloadHistory = async (year: number, markLoaded = false) => {
    setIsLoading(true);
    await request.send(async () => {
      const data = await AdminViewApi.getDownloadHistory();
      setDownloadHistoryData(data);
      if (markLoaded) setHasLoaded(true);
      else setHasLoaded(true);
    });
    setIsLoading(false);
  };

  const onConfirm = async () => {
    await fetchDownloadHistory(selectedYear, true);
  };

  const filteredData = useMemo(() => {
    return downloadHistoryData.filter((entry) => {
      const raw =
        (entry as any)["CREATION_DATE"] ||
        (entry as any)["creation_date"] ||
        (entry as any)["CreationDate"];

      if (!raw) return false;
      const date = new Date(String(raw));
      return !isNaN(date.getTime()) && date.getFullYear() === selectedYear;
    });
  }, [downloadHistoryData, selectedYear]);

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = filteredData) => {
    if (rows.length === 0) return;

    const columns = Object.keys(rows[0] ?? {});
    const escapeCell = (value: unknown): string => {
      const text = value == null ? "" : String(value).replace(/\r?\n/g, " ");
      return `"${text.replace(/"/g, '""')}"`;
    };

    if (format === "csv") {
      const csv = [
        columns.join(","),
        ...rows.map((row) => columns.map((column) => escapeCell((row as Record<string, unknown>)[column])).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `download_history_${selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData = rows.map((row) => {
      const values: Record<string, unknown> = {};
      columns.forEach((column) => {
        values[column] = (row as Record<string, unknown>)[column] ?? "";
      });
      return values;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: columns });
    XLSX.utils.book_append_sheet(workbook, worksheet, "DownloadHistory");

    const workbookBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([workbookBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `download_history_${selectedYear}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : filteredData)}
      />
    </Menu>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.controls}>
        <LabeledSelect
          className={styles.yearSelect}
          items={yearOptions}
          label={t(texts.adminView.year)}
          onSelect={(year) => {
            if (year === undefined) return;
            setSelectedYear(year);
            void fetchDownloadHistory(year);
          }}
          selected={selectedYear}
          renderOptionTitle={(year) => year.toString()}
        />
        <Button
          className={styles.confirmButton}
          intent="primary"
          text={t(texts.adminView.confirm)}
          onClick={onConfirm}
          loading={isLoading}
        />
        {hasLoaded && (
          <div className={styles.countHeader}>{visibleCount} Records Found</div>
        )}
      </div>

      {hasLoaded && (
        <div className={styles.tableContainer}>
          <Table2
            data={filteredData}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noDownloadHistoryData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};
